/*
 * Signal-Store with reference counting (concept B.4).
 *
 * Per plant, we keep exactly ONE hub subscription regardless of how many
 * widgets / pages read from it. Mounts bump a per-plant refcount; unmounts
 * decrement. First 0→1 subscribes, last 1→0 unsubscribes after a short grace
 * period (concept: kurze Karenz gegen Abo-Flattern — 1.5 s here).
 *
 * Visibility gating: when the tab becomes hidden we drop live subscribes after
 * the same 1.5 s grace, and re-subscribe on visibilitychange back to visible.
 * The refcounts stay put during that time — the plant is still "wanted",
 * we just aren't shovelling updates into a hidden DOM.
 *
 * Each leaf id maps to its own reactive Signal inside the plant map. That
 * matters at 5000+ leaves per plant: only the row(s) whose leaf actually
 * received an update re-render, not the whole tree. LeafRow reads its signal
 * with `.value`, so Preact/Signals wires up the fine-grained subscription.
 *
 * The store owns:
 *  - a Map<plantId, PlantEntry> for per-plant refcount + values map
 *  - a single subscription to hubClient.onValues that dispatches into it
 *  - visibility listeners at module scope (one pair for the whole app)
 */

import { signal, type Signal } from '@preact/signals';
import { useEffect, useMemo } from 'preact/hooks';
import {
  onValues,
  onReconnected,
  subscribePlant,
  unsubscribePlant,
  type WireValuesBatch,
} from './hubClient';
// Der Live-Modus haengt an genau denselben drei Punkten wie das Werte-Abo:
// 0->1 anfordern, letztes 1->0 freigeben, Reconnect erneuern. Deshalb hier
// und nicht in einer Seitenkomponente — es gibt mehrere Seiten, die
// Anlagenwerte zeigen, und ein Live-Modus, der an einer davon haengt, waere
// auf den anderen aus.
import { acquireLive, invalidateLiveAfterReconnect, releaseLive } from './liveStore';

/** Quality codes from D.5. `Override` = Force aktiv. `Stale` / `Disconnected`
 *  are set server-side when the driver flagged the point as such. */
export type LeafQuality = 'Good' | 'Bad' | 'Uncertain' | 'Stale' | 'Disconnected' | 'Override';

/** Client-side leaf value: server payload + local receive timestamp. The local
 *  timestamp drives the freshness heuristic — we don't trust wall-clock drift
 *  between server and client for that decision. */
export interface LeafValue {
  v: unknown;
  q: LeafQuality;
  /** Server timestamp (ISO). Kept for display / tooltip. */
  t: string;
  /** Local monotonic-ish receive time (ms since epoch, from `Date.now()`). */
  receivedAt: number;
}

/** Public shape returned by `useLivePlant`. Each leaf id maps to its own
 *  Signal so components read fine-grained. */
export interface PlantLiveState {
  /** Look up the reactive signal for a leaf. Creating on demand keeps the
   *  cost proportional to actually-viewed leaves. */
  leafSignal(leafId: string): Signal<LeafValue | null>;
  /** Ring-buffered recent values for a numeric leaf. Length capped by
   *  <c>LEAF_HISTORY_CAP</c>. Consumers (sparklines) MUST NOT mutate the
   *  returned array — treat it as read-only. */
  leafHistory(leafId: string): readonly number[];
  /** Flips to true once /api/plants/{id}/values has been merged in (or the
   *  snapshot request has failed and we've given up seeding). Consumed by
   *  LeafValueDisplay to distinguish "loading, please spin" from "cache miss,
   *  render em-dash". */
  hydrationSignal: Signal<boolean>;
}

/** Sparkline sample cap per leaf. 30 = ~3 s at 10 Hz push cadence, which is
 *  enough shape to be useful without holding megabytes across 5 000 leaves.
 *  Rings are lazy-allocated per observed leaf. */
const LEAF_HISTORY_CAP = 30;
/** Frozen empty array reused by leafHistory() for leaves that haven't
 *  received any numeric samples yet — avoids allocating a new [] per call. */
const EMPTY_HISTORY: readonly number[] = Object.freeze([]);

/** Grace before we tear down a hub subscription after refcount hits 0, or
 *  before we tear it down after the tab becomes hidden. Concept: "kurze
 *  Karenz (1 bis 2 s) gegen Abo-Flattern". */
// Laenger als ein Wechsel zwischen den Reitern einer Anlage: sonst endete das
// Abo samt Live-Modus bei jedem Klick auf "Datenpunkte" und wieder zurueck,
// und nach einem Schemawechsel dauerte das Fuellen der Werte entsprechend
// lange (Patrick, 16.10.2026).
const SUBSCRIPTION_GRACE_MS = 60_000;

interface PlantEntry {
  refcount: number;
  /** Per-leaf signals. Missing entry = no update yet observed. */
  leafSignals: Map<string, Signal<LeafValue | null>>;
  /** Per-leaf ring buffers of numeric values for sparklines. Lazy — only
   *  populated for leaves whose signal has received a numeric update. */
  leafHistories: Map<string, number[]>;
  /** Set when refcount drops to 0 or tab hides, cleared on re-mount / reveal. */
  teardownTimer: number | null;
  /** True once we've called (and awaited) subscribePlant successfully. */
  subscribed: boolean;
  /** True once the /api/plants/{id}/values snapshot has been merged (or the
   *  merge has been abandoned). Exposed to the UI so leaves render a spinner
   *  until either the snapshot lands or we've decided we're done trying. */
  hydrationSignal: Signal<boolean>;
  /** True while a snapshot fetch is in flight — de-dupes concurrent mounts. */
  snapshotInflight: boolean;
}

const plants = new Map<string, PlantEntry>();

/** Visibility signal: true when the tab is visible. Module-scope so we don't
 *  register N listeners for N plants. */
const isVisible = signal<boolean>(
  typeof document === 'undefined' ? true : document.visibilityState === 'visible',
);

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const nowVisible = document.visibilityState === 'visible';
    isVisible.value = nowVisible;
    if (nowVisible) {
      // Tab came back — re-subscribe every plant that still has viewers, and
      // clear any pending hidden-tab teardown timers.
      for (const [plantId, entry] of plants) {
        if (entry.teardownTimer !== null) {
          clearTimeout(entry.teardownTimer);
          entry.teardownTimer = null;
        }
        if (entry.refcount > 0 && !entry.subscribed) {
          void ensureSubscribed(plantId, entry);
        }
      }
    } else {
      // Tab hidden — after the grace, drop hub subscriptions but keep the
      // refcount + last values (so reveal shows Last-Value-on-Reveal, concept B.4).
      for (const [plantId, entry] of plants) {
        if (entry.refcount > 0 && entry.subscribed && entry.teardownTimer === null) {
          entry.teardownTimer = scheduleTeardown(plantId, entry);
        }
      }
    }
  });
}

// On hub reconnect: server-side group memberships (values-{plantId}) are
// gone because the socket was closed. The store still has `subscribed=true`
// on every plant with a viewer, so no re-subscribe would ever happen
// without explicit help. Flip the flag and re-invoke Subscribe for every
// live plant. Also reset hydration so the next PlantSchemaPage render
// will re-pull the values snapshot (the hub doesn't replay missed pushes,
// so without a re-snapshot we'd keep showing stale values).
onReconnected(() => {
  for (const [plantId, entry] of plants) {
    if (entry.refcount > 0) {
      entry.subscribed = false;
      // Die Live-Sitzungen dieser Verbindung hat der Server beim Disconnect
      // geschlossen. Die tote SessionId wegwerfen, damit das ensureSubscribed
      // darunter eine neue Sitzung oeffnet statt gegen eine Id zu klopfen,
      // die es nicht mehr gibt.
      invalidateLiveAfterReconnect(plantId);
      void ensureSubscribed(plantId, entry);
      entry.hydrationSignal.value = false;
      entry.snapshotInflight = false;
    }
  }
});

// One shared onValues subscription for the whole app, wired at module load.
onValues((batch: WireValuesBatch) => {
  const entry = plants.get(batch.p);
  if (!entry) return; // update for a plant nobody is watching anymore

  const now = Date.now();
  for (const update of batch.u) {
    let sig = entry.leafSignals.get(update.id);
    if (!sig) {
      sig = signal<LeafValue | null>(null);
      entry.leafSignals.set(update.id, sig);
    }
    sig.value = {
      v: update.v,
      q: update.q as LeafQuality,
      t: update.t,
      receivedAt: now,
    };
    // Append to the sparkline ring buffer when the value is numeric. Skipping
    // non-numerics keeps memory bounded on Bool/Enum/String leaves (which
    // wouldn't render a sparkline anyway).
    if (typeof update.v === 'number' && Number.isFinite(update.v)) {
      let hist = entry.leafHistories.get(update.id);
      if (!hist) {
        hist = [];
        entry.leafHistories.set(update.id, hist);
      }
      hist.push(update.v);
      if (hist.length > LEAF_HISTORY_CAP) hist.shift();
    }
  }
});

function scheduleTeardown(plantId: string, entry: PlantEntry): number {
  return window.setTimeout(() => {
    entry.teardownTimer = null;
    // Refcount may have gone up again during the grace, or the tab may have
    // come back visible. In either case, do NOT tear down.
    const wanted = entry.refcount > 0 && isVisible.value;
    if (wanted) return;
    if (!entry.subscribed) return;
    entry.subscribed = false;
    // Live zuerst freigeben: ein versteckter Tab oder eine verlassene Seite
    // soll den Client nicht eine TTL lang weitersenden lassen.
    releaseLive(plantId);
    void unsubscribePlant(plantId);
  }, SUBSCRIPTION_GRACE_MS);
}

async function ensureSubscribed(plantId: string, entry: PlantEntry): Promise<void> {
  if (entry.subscribed) return;
  entry.subscribed = true;
  try {
    await subscribePlant(plantId);
  } catch (err) {
    entry.subscribed = false;
    console.error(`[signalStore] Subscribe(${plantId}) failed`, err);
    return;
  }
  // Erst nach erfolgreichem Subscribe: ohne Werte-Abo waere ein Live-Modus
  // ein Field-Client, der jede Aenderung sendet, ohne dass sie jemand sieht.
  acquireLive(plantId);
}

function acquirePlant(plantId: string): PlantEntry {
  let entry = plants.get(plantId);
  if (!entry) {
    entry = {
      refcount: 0,
      leafSignals: new Map(),
      leafHistories: new Map(),
      teardownTimer: null,
      subscribed: false,
      hydrationSignal: signal<boolean>(false),
      snapshotInflight: false,
    };
    plants.set(plantId, entry);
  }

  const wasZero = entry.refcount === 0;
  entry.refcount += 1;

  // If a teardown was queued because refcount was 0, cancel it — we're back.
  if (entry.teardownTimer !== null) {
    clearTimeout(entry.teardownTimer);
    entry.teardownTimer = null;
  }

  if (wasZero && isVisible.value) {
    void ensureSubscribed(plantId, entry);
  }

  return entry;
}

/**
 * Merge a one-shot server snapshot into the per-leaf signals.
 *
 * Called by PlantSchemaPage after `/api/plants/{id}/values` responds. We only
 * fill signals that are still `null` — an early SignalR push has already
 * populated the freshest known value, and the snapshot's `t` is at best equal
 * (usually older) because it was captured before the subscribe took effect.
 *
 * Flips `hydrationSignal` to true regardless — the point is "we tried",
 * not "we found something for every leaf". Missing leaves remain as spinners
 * until the first live push (concept: server never publishes a value it
 * doesn't have — clients render spinner for those).
 */
export function hydratePlantSnapshot(
  plantId: string,
  entries: Record<string, { v: unknown; q: string; t: string }>,
): void {
  const entry = plants.get(plantId);
  if (!entry) return; // Plant no longer mounted; drop the merge silently.
  const now = Date.now();
  for (const [leafId, snap] of Object.entries(entries)) {
    let sig = entry.leafSignals.get(leafId);
    if (!sig) {
      sig = signal<LeafValue | null>(null);
      entry.leafSignals.set(leafId, sig);
    }
    // Latest-wins nach Zeitstempel des Werts: ein Push, der vor dem Abruf
    // kam, bleibt, wenn er neuer ist. Ein aelterer Wert im Speicher (Seite
    // war verlassen, unveraenderte Werte schickt niemand nach) wird ersetzt.
    if (sig.value !== null && !(Date.parse(snap.t) > Date.parse(sig.value.t))) continue;
    sig.value = {
      v: snap.v,
      q: snap.q as LeafQuality,
      t: snap.t,
      receivedAt: now,
    };
  }
  entry.hydrationSignal.value = true;
}

/**
 * Idempotent helper — flips hydration true without touching any values. Used
 * on snapshot-fetch failure so we stop spinning forever if the endpoint is
 * down: the client falls back to the first live push per leaf, same as before
 * this feature existed.
 */
export function markPlantHydrated(plantId: string): void {
  const entry = plants.get(plantId);
  if (!entry) return;
  entry.hydrationSignal.value = true;
}

/**
 * Track whether a snapshot fetch is currently in flight for this plant.
 * De-dupes concurrent mounts (e.g. two widgets on the same plant + the schema
 * page). Returns true if the caller "won" the race and should perform the
 * fetch; false if another mount already owns it.
 */
export function tryClaimSnapshotFetch(plantId: string): boolean {
  const entry = plants.get(plantId);
  if (!entry) return false;
  // Bei jedem Oeffnen neu holen, nicht nur beim ersten Mal; doppelt
  // laufende Abrufe verhindert snapshotInflight.
  if (entry.snapshotInflight) return false;
  entry.snapshotInflight = true;
  return true;
}

export function releaseSnapshotFetch(plantId: string): void {
  const entry = plants.get(plantId);
  if (!entry) return;
  entry.snapshotInflight = false;
}

function releasePlant(plantId: string): void {
  const entry = plants.get(plantId);
  if (!entry) return;
  entry.refcount = Math.max(0, entry.refcount - 1);
  if (entry.refcount === 0 && entry.teardownTimer === null) {
    entry.teardownTimer = scheduleTeardown(plantId, entry);
  }
}

/**
 * Preact hook: mount to signal interest in a plant's live values.
 *
 * On mount → bumps refcount, triggers subscribe on 0→1.
 * On unmount → decrements refcount, schedules unsubscribe on 1→0 after 1.5 s.
 * Handles plantId changes as an unmount-then-mount pair.
 *
 * Returns an object with `leafSignal(leafId)` — call it once per leaf you want
 * to display; the returned Signal fires on that leaf's updates only.
 */
export function useLivePlant(plantId: string | undefined): PlantLiveState | null {
  useEffect(() => {
    if (!plantId) return undefined;
    acquirePlant(plantId);
    return () => {
      releasePlant(plantId);
    };
  }, [plantId]);

  // Stable accessor per plantId. The `plants` map itself is module-global; we
  // just close over the current plantId.
  return useMemo<PlantLiveState | null>(() => {
    if (!plantId) return null;
    // Make sure the entry exists so we can hand back its hydrationSignal even
    // before the first render's effect runs. Missing entry → create empty.
    let entry = plants.get(plantId);
    if (!entry) {
      entry = {
        refcount: 0,
        leafSignals: new Map(),
        leafHistories: new Map(),
        teardownTimer: null,
        subscribed: false,
        hydrationSignal: signal<boolean>(false),
        snapshotInflight: false,
      };
      plants.set(plantId, entry);
    }
    const hydrationSignal = entry.hydrationSignal;
    return {
      hydrationSignal,
      leafHistory(leafId: string): readonly number[] {
        const e = plants.get(plantId);
        return (e?.leafHistories.get(leafId) ?? EMPTY_HISTORY) as readonly number[];
      },
      leafSignal(leafId: string): Signal<LeafValue | null> {
        // The plant entry may not yet exist during the very first render
        // before the effect runs — create it eagerly so the caller can wire
        // the reactive read without a null hop.
        let entry = plants.get(plantId);
        if (!entry) {
          entry = {
            refcount: 0,
            leafSignals: new Map(),
            leafHistories: new Map(),
            teardownTimer: null,
            subscribed: false,
            hydrationSignal: signal<boolean>(false),
            snapshotInflight: false,
          };
          plants.set(plantId, entry);
        }
        let sig = entry.leafSignals.get(leafId);
        if (!sig) {
          sig = signal<LeafValue | null>(null);
          entry.leafSignals.set(leafId, sig);
        }
        return sig;
      },
    };
  }, [plantId]);
}
